import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useI18n } from '../../i18n/I18nContext'
import type { MfaEnrollmentDetails } from '../../services/auth'
import { MfaEnrollment } from './MfaEnrollment'

interface LoginFormProps {
  onSuccess?: () => void
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const {
    login,
    verifyMfaChallenge,
    startMfaEnrollment,
    confirmMfaEnrollment,
    acknowledgeMfaRecoveryCodes,
    authenticationState,
  } = useAuth()
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [challengeToken, setChallengeToken] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [enrollment, setEnrollment] = useState<MfaEnrollmentDetails | null>(null)
  const [enrollmentExpired, setEnrollmentExpired] = useState(false)
  // Set once the user cancels or the window expires, so we show the manual
  // "start again" view instead of immediately re-fetching a fresh secret.
  const [enrollmentDismissed, setEnrollmentDismissed] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const submitPendingRef = useRef(false)
  const startingEnrollmentRef = useRef(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitPendingRef.current) return
    submitPendingRef.current = true
    setError('')
    setLoading(true)

    try {
      const result = await login(email, password)
      if (result.authenticationState === 'mfa_required' && result.challengeToken) {
        setChallengeToken(result.challengeToken)
      } else if (result.authenticationState === 'mfa_enrollment_required') {
        // The context toggles `loading`, which remounts this form. The pending
        // secret is fetched by the auto-start effect on the fresh instance so it
        // is never set on a dead one.
      } else {
        onSuccess?.()
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error?.message || t('Login failed'))
    } finally {
      submitPendingRef.current = false
      setLoading(false)
    }
  }

  const verifyChallenge = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      await verifyMfaChallenge(challengeToken, verificationCode)
      setVerificationCode('')
      onSuccess?.()
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error?.message || t('MFA verification failed'))
    } finally {
      setLoading(false)
    }
  }

  const confirmEnrollment = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await confirmMfaEnrollment(verificationCode)
      setVerificationCode('')
      setEnrollment(null) // Drop the secret/URI from memory on success.
      setEnrollmentDismissed(true) // Do not auto-start a new enrollment.
      setRecoveryCodes(result.recoveryCodes || [])
    } catch (err: any) {
      if (err.response?.status === 401) {
        setEnrollment(null); setVerificationCode(''); setEnrollmentDismissed(false); setEnrollmentExpired(false)
      }
      setError(err.response?.data?.message || err.response?.data?.error?.message || t('MFA enrollment failed'))
    } finally {
      setLoading(false)
    }
  }

  // Fetch a fresh pending enrollment as soon as the account is required to
  // enroll and nothing is pending. Runs on the (re)mounted form instance so the
  // secret is never set on a form the context's loading toggle has unmounted.
  useEffect(() => {
    if (authenticationState !== 'mfa_enrollment_required') return
    if (enrollment || enrollmentDismissed || recoveryCodes.length > 0) return
    if (startingEnrollmentRef.current) return
    startingEnrollmentRef.current = true
    setLoading(true)
    startMfaEnrollment()
      .then((started) => {
        setEnrollment(started.enrollment)
        setEnrollmentExpired(false)
      })
      .catch((err: any) => {
        setError(err?.response?.data?.message || err?.response?.data?.error?.message || t('MFA enrollment failed'))
      })
      .finally(() => {
        startingEnrollmentRef.current = false
        setLoading(false)
      })
    // startMfaEnrollment/t are stable enough; the refs and guards above prevent
    // duplicate fetches regardless of identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationState, enrollment, enrollmentDismissed, recoveryCodes.length])

  const restartEnrollment = () => {
    // Clearing the dismissed flag re-triggers the auto-start effect above.
    setError('')
    setEnrollmentExpired(false)
    setVerificationCode('')
    setEnrollmentDismissed(false)
  }

  const cancelEnrollment = () => {
    // Clear the secret/URI from memory when the user abandons enrollment.
    setEnrollment(null)
    setEnrollmentDismissed(true)
    setVerificationCode('')
    setError('')
    setEnrollmentExpired(false)
  }

  const expireEnrollment = () => {
    // The pending window elapsed; drop the secret and prompt a restart.
    setEnrollment(null)
    setEnrollmentDismissed(true)
    setVerificationCode('')
    setEnrollmentExpired(true)
  }

  const acknowledgeRecoveryCodes = async () => {
    if (submitPendingRef.current) return
    submitPendingRef.current = true
    setLoading(true)
    setError('')
    try {
      await acknowledgeMfaRecoveryCodes()
      setRecoveryCodes([])
      onSuccess?.()
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error?.message || t('Unable to load this section. Please try again.'))
    } finally {
      submitPendingRef.current = false
      setLoading(false)
    }
  }

  if (recoveryCodes.length > 0) {
    return (
      <section className="stack" aria-labelledby="mfa-recovery-heading">
        <h2 id="mfa-recovery-heading">{t('Save your recovery codes')}</h2>
        <p>{t('Each code can be used once. Store them securely; they will not be shown again.')}</p>
        <pre className="code-block" data-testid="mfa-recovery-codes">{recoveryCodes.join('\n')}</pre>
        {error && <p role="alert">{t(error)}</p>}
        <button type="button" className="btn btn-primary" disabled={loading} onClick={acknowledgeRecoveryCodes}>
          {t('I saved these recovery codes')}
        </button>
      </section>
    )
  }

  if (enrollment || authenticationState === 'mfa_enrollment_required') {
    return (
      <MfaEnrollment
        enrollment={enrollment}
        loading={loading}
        error={error}
        expired={enrollmentExpired}
        verificationCode={verificationCode}
        onVerificationCodeChange={setVerificationCode}
        onConfirm={confirmEnrollment}
        onStart={restartEnrollment}
        onCancel={cancelEnrollment}
        onExpire={expireEnrollment}
      />
    )
  }

  if (challengeToken) {
    return (
      <form onSubmit={verifyChallenge} className="stack" data-testid="mfa-challenge-form">
        <h2>{t('Verify your sign-in')}</h2>
        <p>{t('Enter an authenticator code or a one-time recovery code.')}</p>
        <div className="form-row">
          <label htmlFor="mfaChallengeCode">{t('Verification code')}</label>
          <input id="mfaChallengeCode" className="input" autoComplete="one-time-code" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value)} required />
        </div>
        {error && <p className="error-text">{t(error)}</p>}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? t('Verifying...') : t('Verify')}
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="stack" data-testid="login-form">
      <div className="form-row">
        <label htmlFor="loginEmail">{t('Email')}</label>
        <input id="loginEmail" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder={t('Enter your email')} required />
        <p className="helper">{t('Use the email provisioned for your admin identity.')}</p>
      </div>

      <div className="form-row">
        <label htmlFor="loginPassword">{t('Password')}</label>
        <input id="loginPassword" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" placeholder={t('Enter your password')} required />
      </div>

      {error && <p className="error-text">{t(error)}</p>}

      <button type="submit" disabled={loading} className="btn btn-primary" data-testid="login-submit">
        {loading ? t('Signing in...') : t('Sign in')}
      </button>
      <a href="/forgot-password">{t('Forgot password?')}</a>
    </form>
  )
}
